import { TRANSITIONS } from '@lixionary-jira/domain';
import { BookOpen, Eye, EyeOff, Hammer, PackageCheck, Rocket, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router';
import { api } from '../../../api/client';
import { useAction } from '../../../api/hooks';
import { useMe } from '../../../api/session';
import type { TicketDetail } from '../../../api/ticket-types';
import { StatusBadge, humanize } from '../../../ui/Badge';
import { Button } from '../../../ui/Button';
import { Alert, ErrorAlert } from '../../../ui/feedback';
import { ActorName } from '../../../ui/misc';
import { Timestamp } from '../../../ui/Timestamp';
import { type DialogAction } from '../ActionDialog';
import { ExternalReporterLabel, PriorityBadge, TicketFlags, TypeLabel } from '../bits';

export function Header({
  t,
  onAction,
  onEdit,
}: {
  t: TicketDetail;
  onAction: (a: DialogAction) => void;
  onEdit: () => void;
}) {
  const me = useMe();
  const watch = useAction(
    () =>
      t.watching
        ? api.del(`/projects/${t.project.key}/tickets/${t.number}/watch`)
        : api.put(`/projects/${t.project.key}/tickets/${t.number}/watch`),
    { invalidate: [['ticket', t.project.key, t.number]] },
  );
  const archive = useAction(
    () =>
      api.post(`/projects/${t.project.key}/tickets/${t.number}/${t.archived ? 'unarchive' : 'archive'}`, {
        expectedVersion: t.version,
      }),
    { success: t.archived ? 'Unarchived' : 'Archived', invalidate: [['ticket', t.project.key, t.number]] },
  );
  const primary = t.viewer.actions.filter((a) => a === 'accept');
  const others = t.viewer.actions.filter(
    (a) => a !== 'accept' && a !== 'mark_ready' && a !== 'rework_in_progress',
  );
  return (
    <div className="stack sm">
      <div className="row small muted" style={{ gap: 12 }}>
        <span className="mono ink">{t.key}</span>
        <TypeLabel type={t.type} />
        <span>Scope revision {t.scopeRevision}</span>
        <span>
          Reported by{' '}
          {t.externalReporter ? (
            <>
              <ExternalReporterLabel reporter={t.externalReporter} /> · submitted by{' '}
              <ActorName actor={t.reporter} />
            </>
          ) : (
            <ActorName actor={t.reporter} />
          )}{' '}
          · <Timestamp value={t.createdAt} />
        </span>
      </div>
      <div className="page-header">
        <div className="titles">
          <h1 className="page-title" style={{ fontSize: 32 }}>
            {t.title}
          </h1>
          <div className="row">
            <StatusBadge status={t.status} resolution={t.resolution} />
            <PriorityBadge priority={t.priority} />
            <TicketFlags t={t} />
            {t.labels.map((l) => (
              <span key={l} className="chip">
                {l}
              </span>
            ))}
          </div>
        </div>
        <div className="actions">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => watch.mutate(undefined)}
            icon={t.watching ? <EyeOff size={14} /> : <Eye size={14} />}
          >
            {t.watching ? 'Unwatch' : 'Watch'}
          </Button>
          {t.viewer.canEditScope && (
            <Button variant="secondary" size="sm" onClick={onEdit}>
              Edit scope
            </Button>
          )}
          {others.map((a) => (
            <Button
              key={a}
              size="sm"
              variant={a === 'reject' ? 'danger-outline' : 'secondary'}
              onClick={() => onAction(a === 'rework_todo' ? 'rework' : a)}
            >
              {a === 'rework_todo'
                ? 'Rework'
                : a === 'release_claim'
                  ? 'Return to Todo'
                  : TRANSITIONS[a].label}
            </Button>
          ))}
          {t.viewer.canStartManual && (
            <Button size="sm" variant="secondary" onClick={() => onAction('start_manual')}>
              Take manual ownership
            </Button>
          )}
          {t.status === 'closed' && me.user.role === 'admin' && (
            <Button size="sm" variant="ghost" onClick={() => archive.mutate(undefined)}>
              {t.archived ? 'Unarchive' : 'Archive'}
            </Button>
          )}
          {primary.map((a) => (
            <Button key={a} size="sm" onClick={() => onAction(a)}>
              Accept current scope
            </Button>
          ))}
        </div>
      </div>
      {t.project.archived && <Alert tone="info">This project is archived. The ticket is read-only.</Alert>}
      {t.status === 'closed' && t.resolution === 'rejected' && t.rejectionReason && (
        <Alert tone="info" title="Rejected">
          {t.rejectionReason}
        </Alert>
      )}
      {t.needsClarification && (
        <Alert tone="warning" title="Needs clarification">
          {t.clarificationReason}
        </Alert>
      )}
      {t.staleOwner && t.owner && (
        <Alert tone="error" title="Stale owner">
          <span>
            {t.owner.name}'s claim expired without a heartbeat. Work writes are fenced until an admin recovers
            it.{' '}
            <Link to={`/projects/${t.project.key}/tickets/${t.number}/implementations/${t.owner.displayId}`}>
              Open {t.owner.displayId}
            </Link>
          </span>
        </Alert>
      )}
      <ErrorAlert error={watch.error ?? archive.error} />
    </div>
  );
}

/** INT-03 traceability: scope → context → acceptance → implementation → release approval → delivery. */
export function Trace({ t }: { t: TicketDetail }) {
  const base = `/projects/${t.project.key}/tickets/${t.number}`;
  const acc = t.acceptances.find((a) => a.current) ?? t.acceptances[0];
  const impl = t.implementations[0];
  const rel = t.releases[0];
  const del = t.deliveries[0];
  const external = t.externalDeliveries?.[0];
  const latestExternal =
    external && (!del || Date.parse(external.recordedAt) >= Date.parse(del.registeredAt)) ? external : null;
  const steps = [
    { icon: BookOpen, label: 'Scope', value: `Revision ${t.scopeRevision}`, to: `${base}?tab=history` },
    {
      icon: ShieldCheck,
      label: 'Acceptance',
      value: acc ? `${acc.displayId}${acc.revokedAt ? ' (revoked)' : ''}` : 'Not accepted',
      to: `${base}?tab=approvals`,
    },
    {
      icon: Hammer,
      label: 'Implementation',
      value: impl ? `${impl.displayId} · ${humanize(impl.state)}` : '—',
      to: impl ? `${base}/implementations/${impl.displayId}` : undefined,
    },
    {
      icon: Rocket,
      label: 'Release',
      value: rel ? `${rel.displayId} · ${humanize(rel.state)}` : '—',
      to: rel ? `/projects/${t.project.key}/releases/${rel.displayId}` : undefined,
    },
    {
      icon: PackageCheck,
      label: 'Delivery',
      value: latestExternal
        ? 'Existing deployment · verified'
        : del
          ? `ATT-${del.number} · ${humanize(del.outcome ?? del.state)}${del.provenance ? ` (${del.provenance})` : ''}`
          : '—',
      to: latestExternal
        ? `/projects/${t.project.key}/releases/${latestExternal.releaseDisplayId}`
        : del
          ? `/projects/${t.project.key}/releases/${del.releaseDisplayId}/attempts/ATT-${del.number}`
          : undefined,
    },
  ];
  return (
    <nav className="trace" aria-label="Request to delivery trace">
      {steps.map((s) => (
        <div key={s.label} className="trace-step">
          <span className="row nowrap small muted" style={{ gap: 6 }}>
            <s.icon size={14} aria-hidden /> {s.label}
          </span>
          {s.to ? (
            <Link to={s.to} className="small">
              {s.value}
            </Link>
          ) : (
            <span className="small ink">{s.value}</span>
          )}
        </div>
      ))}
    </nav>
  );
}
